import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Pagination,
  Paper,
  Typography,
} from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import TitleIcon from "@mui/icons-material/Title";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { highlightText, paginate } from "../bible/searchBible";

const ink = {
  chip: {
    height: 22,
    fontSize: "0.7rem",
    borderRadius: 1,
    bgcolor: "rgba(139, 105, 20, 0.08)",
    color: "#8b6914",
    border: "none",
  },
};

const Highlighted = ({ text, term, sx }) => {
  const parts = highlightText(text, term);
  if (!Array.isArray(parts)) {
    return (
      <Box component="span" sx={sx}>
        {text}
      </Box>
    );
  }
  return (
    <Box component="span" sx={sx}>
      {parts.map((part, index) =>
        typeof part === "string" ? (
          part
        ) : (
          <Box
            component="mark"
            key={part.key || index}
            sx={{
              bgcolor: "rgba(196, 162, 101, 0.35)",
              color: "inherit",
              fontWeight: 700,
              px: "2px",
              borderRadius: "3px",
            }}
          >
            {part.text}
          </Box>
        )
      )}
    </Box>
  );
};

const ResultCard = ({ children, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.(e);
      }
    }}
    role="button"
    tabIndex={0}
    sx={{
      mb: 1.25,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      overflow: "hidden",
      cursor: "pointer",
      transition: "border-color 0.15s, box-shadow 0.15s, background-color 0.15s",
      "&:hover": {
        borderColor: "primary.main",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      },
      "&:focus-visible": {
        outline: "2px solid",
        outlineColor: "primary.main",
      },
    }}
  >
    <Box sx={{ p: 1.75 }}>{children}</Box>
  </Paper>
);

const SectionHeader = ({ icon, label, count }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      mt: 1,
      mb: 1.25,
    }}
  >
    {icon}
    <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {count}
    </Typography>
  </Box>
);

const VerseSection = ({
  ui,
  term,
  items,
  total,
  page,
  onPageChange,
  onOpenChapter,
}) => (
  <Box>
    <SectionHeader
      icon={<FormatQuoteIcon fontSize="small" color="primary" />}
      label={ui.groupVerses}
      count={total}
    />
    {items.map((item) => {
      const lead = item.verses[0];
      const extra = item.verses.length - 1;
      return (
        <ResultCard
          key={item.chapter.id}
          onClick={() => onOpenChapter(item.chapter.id, lead?.number)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "primary.main", mb: 0.75 }}
              >
                {item.book} {item.chapter.chapterNumber}:{lead?.number}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <Highlighted text={lead?.text || ""} term={term} />
              </Typography>
              {extra > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75 }}
                >
                  {ui.moreInChapter(extra)}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              endIcon={<ChevronRightIcon />}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              {ui.openChapter || "阅读本章"}
            </Button>
          </Box>
        </ResultCard>
      );
    })}
    {page.totalPages > 1 && (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={page.totalPages}
          page={page.page}
          onChange={(_, value) => onPageChange(value)}
          color="primary"
          size="small"
        />
      </Box>
    )}
  </Box>
);

const SearchResults = ({
  results,
  term,
  ui,
  page,
  onPageChange,
  onOpenBook,
  onOpenChapter,
}) => {
  const [scope, setScope] = useState("all");
  const { books, headingHits, verseHits, query } = results;
  const pinVerses = Boolean(query?.verse);
  const showBooks = scope === "all" || scope === "books";
  const showHeadings = scope === "all" || scope === "headings";
  const showVerses = scope === "all" || scope === "verses";

  const pagedVerses = useMemo(
    () => paginate(showVerses ? verseHits : [], page, 12),
    [page, showVerses, verseHits]
  );

  const reasonLabel = {
    name: ui.bookMatchName,
    pinyin: ui.bookMatchPinyin,
    contains: ui.bookMatchContains,
    heading: ui.headingMatch,
    title: ui.headingMatch,
    reference: ui.refMatch,
  };

  const filters = [
    { id: "all", label: ui.filterAll, count: books.length + headingHits.length + verseHits.length },
    { id: "books", label: ui.groupBooks, count: books.length },
    { id: "headings", label: ui.groupHeadings, count: headingHits.length },
    { id: "verses", label: ui.groupVerses, count: verseHits.length },
  ].filter((item) => item.id === "all" || item.count > 0);

  const totalVisible =
    (showBooks ? books.length : 0) +
    (showHeadings ? headingHits.length : 0) +
    (showVerses ? verseHits.length : 0);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          {ui.searchResults(term)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {ui.foundCount(books.length + headingHits.length + verseHits.length)}
        </Typography>
        {filters.length > 2 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.25 }}>
            {filters.map((item) => (
              <Chip
                key={item.id}
                label={`${item.label} ${item.count}`}
                size="small"
                onClick={() => {
                  setScope(item.id);
                  onPageChange(1);
                }}
                variant={scope === item.id ? "filled" : "outlined"}
                color={scope === item.id ? "primary" : "default"}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        )}
      </Box>

      {totalVisible === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
          {ui.foundCount(0)}
        </Typography>
      )}

      {showVerses && pinVerses && verseHits.length > 0 && (
        <VerseSection
          ui={ui}
          term={term}
          items={pagedVerses.items}
          total={verseHits.length}
          page={pagedVerses}
          onPageChange={onPageChange}
          onOpenChapter={onOpenChapter}
        />
      )}

      {showBooks && books.length > 0 && (
        <Box>
          <SectionHeader
            icon={<MenuBookIcon fontSize="small" color="primary" />}
            label={ui.groupBooks}
            count={books.length}
          />
          {books.map((item) => (
            <ResultCard key={item.book} onClick={() => onOpenBook(item.book)}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
                    <Highlighted text={item.book} term={term} />
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.75, alignItems: "center" }}>
                    <Chip size="small" label={reasonLabel[item.reason]} sx={ink.chip} />
                    <Typography variant="caption" color="text.secondary">
                      {ui.chapterCountLabel(item.chapterCount)}
                    </Typography>
                  </Box>
                </Box>
                <Button size="small" endIcon={<ChevronRightIcon />} sx={{ textTransform: "none" }}>
                  {ui.openBook}
                </Button>
              </Box>
            </ResultCard>
          ))}
        </Box>
      )}

      {showHeadings && headingHits.length > 0 && (
        <Box>
          <SectionHeader
            icon={<TitleIcon fontSize="small" color="primary" />}
            label={ui.groupHeadings}
            count={headingHits.length}
          />
          {headingHits.map((item) => (
            <ResultCard
              key={item.chapter.id}
              onClick={() => onOpenChapter(item.chapter.id)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main" }}>
                    {item.chapter.title.replace("# ", "")}
                  </Typography>
                  {item.heading && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                      <Highlighted text={item.heading} term={term} />
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    label={reasonLabel[item.reason]}
                    sx={{ ...ink.chip, mt: 0.75 }}
                  />
                </Box>
                <Button
                  size="small"
                  endIcon={<ChevronRightIcon />}
                  sx={{ textTransform: "none", flexShrink: 0 }}
                >
                  {ui.openChapter || "阅读本章"}
                </Button>
              </Box>
            </ResultCard>
          ))}
        </Box>
      )}

      {showVerses && !pinVerses && verseHits.length > 0 && (
        <VerseSection
          ui={ui}
          term={term}
          items={pagedVerses.items}
          total={verseHits.length}
          page={pagedVerses}
          onPageChange={onPageChange}
          onOpenChapter={onOpenChapter}
        />
      )}
    </Box>
  );
};

export default SearchResults;
